import format from "string-format";
import log, { LogLevelDesc, Logger } from "loglevel";
import { reg, apply } from "loglevel-plugin-prefix";

import { default as chalk } from "chalk";

const colors: { [key: string]: any } = {
    TRACE: chalk.magenta,
    DEBUG: chalk.cyan,
    INFO: chalk.blue,
    WARN: chalk.yellow,
    ERROR: chalk.red,
};

/**
 * Implements basic logging support that currently will display in the console in the form
 * [date-time] [ {MODULE} ] {LEVEL} : {MSG}.
 * 
 * To output a log at warning level:
 * 
 *      getModuleLog(module-name).warn(msg, args)
 * 
 * Logs can be one of the following levels
 * 
 *      debug, info, warn, error, fatal
 * 
 * The msg can contain placeholders which will be replace by the args passed to the logging function.
 * 
        log.error("Error is {}", "really bad")
 * 
 * Args can also be complex objects and they with be JSON stringified before replacement
 * 
 *      log.info("User signed in {0}", {name:"bob", age:"42"})
 * 
 * For more information on the string interpolation used see
 * https://github.com/davidchambers/string-format
 */
export class ModuleLog {
    private _log: Logger;
    constructor(private _module: string) {
        this._log = log.getLogger(_module);
        this._log.setLevel(log.getLevel(), false);
        apply(this._log, {
            timestampFormatter(date) {
                return date.toISOString();
            },
            levelFormatter(level) {
                return level.toUpperCase();
            },
            nameFormatter(name) {
                return name || "global";
            },
            format(level: string, name: string | undefined, timestamp: Date) {
                level = colors[level.toUpperCase()](level.padEnd(5));

                name = chalk.green(`${name?.padEnd(12)}`);

                return `${chalk.gray(`[${timestamp}]`)} [ ${chalk.green(`${name}`)} ] ${level} : `;
            },
        });
    }

    public get name() {
        return this._module;
    }

    public startTimer(): number {
        return performance.now();
    }

    public endTimer(name: string, startTime: number) {
        const elapsed = performance.now() - startTime;
        this.debug(`${name} took ${elapsed.toFixed(0)} ms`);
    }

    public fatal(msg: string, ...args: any[]) {
        this.log("fatal", msg, args);
    }

    public error(msg: string, ...args: any[]) {
        this.log("error", msg, args);
    }
    public warn(msg: string, ...args: any[]) {
        this.log("warn", msg, args);
    }
    public info(msg: string, ...args: any[]) {
        this.log("info", msg, args);
    }
    public debug(msg: string, ...args: any[]) {
        this.log("debug", msg, args);
    }
    public trace(msg: string, ...args: any[]) {
        this.log("trace", msg, args);
    }

    private log(level: string, msg: string, ...args: any[]) {
        const jsonArgs: string[] = [];
        args.forEach((arg) => {
            jsonArgs.push(JSON.stringify(arg));
        });
        const formattedMsg = format(msg, jsonArgs);
        let logFunc = this._log.log;
        switch (level) {
            case "trace":
                logFunc = this._log.trace;
                break;
            case "debug":
                logFunc = this._log.debug;
                break;
            case "info":
                logFunc = this._log.info;
                break;
            case "warn":
                logFunc = this._log.warn;
                break;
            case "error":
                logFunc = this._log.error;
                break;
            case "fatal":
                logFunc = this._log.error;
                break;
        }
        logFunc(formattedMsg);
    }
}

const MODULE_LOGGERS = new Map<string, ModuleLog>();
let LOGGING_INITIALISED = false;

export function getModuleLog(module: string): ModuleLog {
    if (!LOGGING_INITIALISED) {
        reg(log);
        LOGGING_INITIALISED = true;
    }

    if (MODULE_LOGGERS.has(module)) {
        return MODULE_LOGGERS.get(module) as ModuleLog;
    }
    const moduleLog = new ModuleLog(module);
    MODULE_LOGGERS.set(module, moduleLog);
    return moduleLog;
}

export function setLogLevel(level: string) {
    log.setLevel(level as LogLevelDesc, false);
}
